import React, { useState, useEffect, useRef } from 'react';

interface DocumentPreviewProps {
  fileId: string;
  fileName: string;
  contentType?: string;
  onClose: () => void;
}

interface PreviewResponseData {
  type?: string;
  view_url?: string;
  content_type?: string;
  file_name?: string;
  message?: string;
  timestamp?: number;
}

// Create a cache for document preview data
interface PreviewCache {
  [fileId: string]: {
    data: PreviewResponseData;
    timestamp: number;
  };
}

// Document preview cache shared across all instances
const documentPreviewCache: PreviewCache = {};
const PREVIEW_CACHE_DURATION = 60000; // 1 minute in milliseconds

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ 
  fileId, 
  fileName, 
  contentType, 
  onClose 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponseData | null>(null);
  
  // Use a ref to track ongoing request
  const previewRequestRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    // Function to fetch preview information
    const fetchPreviewData = async () => {
      // If there's already a request in progress for this file, wait for it
      if (previewRequestRef.current) {
        await previewRequestRef.current;
        return;
      }
      
      // Check cache first
      const cachedData = documentPreviewCache[fileId];
      const now = Date.now();
      
      if (cachedData && now - cachedData.timestamp < PREVIEW_CACHE_DURATION) {
        console.debug(`Using cached preview data for file: ${fileName} (ID: ${fileId})`);
        setPreviewData(cachedData.data);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      // Create a new request promise
      const requestPromise = (async () => {
        try {
          // First try to get JSON info in case it's a Google Workspace document
          const response = await fetch(`/api/v1/documents/view/${fileId}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            }
          });
          
          // Check if response is successful
          if (!response.ok) {
            console.error(`Failed to fetch document ${fileName}: ${response.status} ${response.statusText}`);
            throw new Error(`Errore ${response.status}: ${response.statusText}`);
          }
          
          // Check if response is JSON
          const responseContentType = response.headers.get('content-type');
          let responseData: PreviewResponseData;
          
          console.debug(`Response for ${fileName}: status=${response.status}, content-type=${responseContentType}`);
          
          if (responseContentType && responseContentType.includes('application/json')) {
            const data = await response.json();
            responseData = data;
            console.debug(`JSON response data:`, responseData);
          } else {
            // If not JSON, it's a direct binary response (PDF, etc.)
            // We'll use iframe for these, so set a simple data object
            responseData = {
              type: 'binary',
              content_type: responseContentType || 'application/octet-stream',
              file_name: fileName
            };
            console.debug(`Binary response data:`, responseData);
          }
          
          // Cache the preview data
          documentPreviewCache[fileId] = {
            data: responseData,
            timestamp: Date.now()
          };
          
          setPreviewData(responseData);
        } catch (err) {
          console.error('Error fetching document preview:', err);
          setError('Impossibile caricare l\'anteprima del documento');
        } finally {
          setLoading(false);
          previewRequestRef.current = null;
        }
      })();
      
      // Store the request promise
      previewRequestRef.current = requestPromise;
      
      await requestPromise;
    };

    if (fileId) {
      fetchPreviewData();
    }
    
    // Cleanup function to handle component unmount
    return () => {
      // Can't cancel fetch directly, but we can mark the component as unmounted
      previewRequestRef.current = null;
    };
  }, [fileId, fileName, contentType]);

  // Function to render appropriate preview based on content type
  const renderPreview = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="w-12 h-12 border-4 border-blue-600 dark:border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-6">
          <svg className="w-12 h-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-gray-700 dark:text-gray-300 text-center">{error}</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            onClick={() => {
              const apiBaseUrl = import.meta.env.NODE_ENV === 'production' 
                ? 'https://kms-api.qutec.it/api/v1'
                : (import.meta.env.VITE_API_BASE_URL || '/api/v1');
              window.open(`${apiBaseUrl}/documents/view/${fileId}`, '_blank');
            }}
          >
            Apri in nuova scheda
          </button>
        </div>
      );
    }
    
    if (!previewData) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600 dark:text-gray-400">Nessuna anteprima disponibile</p>
        </div>
      );
    }

    // If it's a Google Workspace document
    if (previewData.type === 'google_workspace' && previewData.view_url) {
      return (
        <iframe 
          src={previewData.view_url}
          className="w-full h-full"
          title={fileName}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
          loading="lazy"
        ></iframe>
      );
    }
    
    // For PDF files or other binary content that can be displayed
    if (previewData.type === 'binary' || 
        (contentType === 'application/pdf' || 
         contentType?.startsWith('image/'))) {
      // Use a cache-friendly URL by adding a timestamp parameter once to avoid browser caching
      // but not regenerating it on re-renders
      const cacheKey = previewData.timestamp || Date.now();
      // For iframe requests, we need to use the full API URL since iframe src doesn't go through proxy
      const apiBaseUrl = import.meta.env.NODE_ENV === 'production' 
        ? 'https://kms-api.qutec.it/api/v1'
        : (import.meta.env.VITE_API_BASE_URL || '/api/v1');
      return (
        <iframe
          src={`${apiBaseUrl}/documents/view/${fileId}?cache=${cacheKey}`}
          className="w-full h-full"
          title={fileName}
          loading="lazy"
        ></iframe>
      );
    }
    
    // For other binary content that can be displayed (images, etc.)
    if (previewData.type === 'binary' || contentType?.startsWith('image/') || previewData.content_type?.startsWith('image/')) {
      // Use a cache-friendly URL by adding a timestamp parameter once to avoid browser caching
      // but not regenerating it on re-renders
      const cacheKey = previewData.timestamp || Date.now();
      // For iframe requests, we need to use the full API URL since iframe src doesn't go through proxy
      const apiBaseUrl = import.meta.env.NODE_ENV === 'production' 
        ? 'https://kms-api.qutec.it/api/v1'
        : (import.meta.env.VITE_API_BASE_URL || '/api/v1');
      return (
        <iframe
          src={`${apiBaseUrl}/documents/view/${fileId}?cache=${cacheKey}`}
          className="w-full h-full"
          title={fileName}
          loading="lazy"
          sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
        ></iframe>
      );
    }

    // For DOCX files
    if (contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      // Use a cache-friendly URL for DOCX files
      const cacheKey = previewData.timestamp || Date.now();
      // For iframe requests, we need to use the full API URL since iframe src doesn't go through proxy
      const apiBaseUrl = import.meta.env.NODE_ENV === 'production' 
        ? 'https://kms-api.qutec.it/api/v1'
        : (import.meta.env.VITE_API_BASE_URL || '/api/v1');
      return (
        <iframe
          src={`${apiBaseUrl}/documents/view/${fileId}?cache=${cacheKey}`}
          className="w-full h-full"
          title={fileName}
          loading="lazy"
        ></iframe>
      );
    }
    
    // Error case
    if (previewData.type === 'error' && previewData.message) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-6">
          <svg className="w-12 h-12 text-yellow-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-gray-700 dark:text-gray-300 text-center">{previewData.message}</p>
          <button 
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            onClick={() => {
              const apiBaseUrl = import.meta.env.NODE_ENV === 'production' 
                ? 'https://kms-api.qutec.it/api/v1'
                : (import.meta.env.VITE_API_BASE_URL || '/api/v1');
              window.open(`${apiBaseUrl}/documents/download/${fileId}`, '_blank');
            }}
          >
            Scarica il file
          </button>
        </div>
      );
    }

    // Fallback for unsupported types
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <svg className="w-12 h-12 text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
        <p className="text-gray-700 dark:text-gray-300 text-center">
          Anteprima non disponibile per questo tipo di file ({contentType || 'tipo sconosciuto'})
        </p>
        <div className="flex mt-4 space-x-4">
          <button 
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            onClick={() => {
              const apiBaseUrl = import.meta.env.NODE_ENV === 'production' 
                ? 'https://kms-api.qutec.it/api/v1'
                : (import.meta.env.VITE_API_BASE_URL || '/api/v1');
              window.open(`${apiBaseUrl}/documents/view/${fileId}`, '_blank');
            }}
          >
            Apri in nuova scheda
          </button>
          <button 
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            onClick={() => {
              const apiBaseUrl = import.meta.env.NODE_ENV === 'production' 
                ? 'https://kms-api.qutec.it/api/v1'
                : (import.meta.env.VITE_API_BASE_URL || '/api/v1');
              window.open(`${apiBaseUrl}/documents/download/${fileId}`, '_blank');
            }}
          >
            Scarica
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-6xl w-full h-5/6 overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center bg-gray-100 dark:bg-gray-700 px-6 py-4 border-b dark:border-gray-600">
          <div className="flex items-center">
            <h2 className="text-xl font-medium text-gray-800 dark:text-gray-100">{fileName}</h2>
            {contentType && (
              <span className="ml-3 px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                {contentType}
              </span>
            )}
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            aria-label="Chiudi"
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="flex-1 overflow-auto">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview; 